"use client";

/**
 * Browser-side GA4 event tracking via gtag.js.
 *
 * We do NOT check consent here. Gtag handles consent mode natively:
 * when analytics_storage is denied, gtag sends cookieless pings.
 * When consent is later granted, gtag retroactively processes events.
 * Adding our own consent check would prevent gtag from receiving events
 * that it needs to handle (e.g. view_item on a landing page before
 * the user accepts cookies).
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

type GA4EventPayload = {
  eventName: string;
  params: Record<string, unknown>;
};

let ga4Configured = false;
const pendingEvents: GA4EventPayload[] = [];

function getGtag(): ((...args: unknown[]) => void) | undefined {
  if (typeof window === "undefined") return undefined;
  return typeof window.gtag === "function" ? window.gtag : undefined;
}

function flushPendingEvents(): boolean {
  const gtag = getGtag();
  if (!gtag || !ga4Configured) {
    return false;
  }

  while (pendingEvents.length > 0) {
    const event = pendingEvents.shift();
    if (!event) {
      continue;
    }
    gtag("event", event.eventName, event.params);
  }

  return true;
}

// ---------------------------------------------------------------------------
// Core event push
// ---------------------------------------------------------------------------

export function pushGA4Event(
  eventName: string,
  params: Record<string, unknown>,
): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  pendingEvents.push({ eventName, params });
  flushPendingEvents();
  return true;
}

export function markGA4Configured(): boolean {
  ga4Configured = true;
  return flushPendingEvents();
}

export function isGA4Ready(): boolean {
  return ga4Configured && Boolean(getGtag());
}

// ---------------------------------------------------------------------------
// Enhanced Conversions - user_data
// ---------------------------------------------------------------------------

export interface GA4UserData {
  email?: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Set user data for Enhanced Conversions.
 * Call before firing a conversion event (purchase, sign_up, generate_lead).
 * gtag hashes plain-text values automatically.
 */
export function setGA4UserData(user: GA4UserData): boolean {
  const gtag = getGtag();
  if (!gtag) return false;

  const userData: Record<string, string> = {};
  if (user.email) userData.email = user.email;
  if (user.firstName) userData.first_name = user.firstName;
  if (user.lastName) userData.last_name = user.lastName;

  if (Object.keys(userData).length === 0) return false;
  gtag("set", "user_data", userData);
  return true;
}

export function resetGA4BrowserStateForTests(): void {
  ga4Configured = false;
  pendingEvents.length = 0;
}
